import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spin, Tag, Typography, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import ErrorPanel from "../../components/delivery/ErrorPanel";
import VideoReviewPlayer from "../../components/videoReview/VideoReviewPlayer";
import VideoReviewComments from "../../components/videoReview/VideoReviewComments";
import { getVideoReview } from "../../utils/videoReviewAdminApi";
import { toDeliveryPublicUrl } from "../../utils/deliveryPath";
import { useTranslation } from "../../i18n";
import "../delivery/delivery.css";
import "./videoReview.css";

const { Text } = Typography;

function VideoReviewAudit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const playerRef = useRef(null);

    const [link, setLink] = useState(null);
    const [status, setStatus] = useState("loading");
    const [currentTimeMs, setCurrentTimeMs] = useState(0);

    const loadLink = useCallback(async () => {
        setStatus("loading");
        try {
            const found = await getVideoReview(id);
            if (!found) {
                setLink(null);
                setStatus("invalid");
                return;
            }
            setLink(found);
            setStatus("ready");
        } catch (err) {
            if (err?.status === 404) {
                setLink(null);
                setStatus("invalid");
                return;
            }
            setLink(null);
            setStatus("error");
            message.error(t("videoReview.admin.manageLoadFailed"));
        }
    }, [id, t]);

    useEffect(() => {
        loadLink();
    }, [loadLink]);

    const videoUrl = useMemo(
        () => (link?.videoPath ? toDeliveryPublicUrl(link.videoPath) : null),
        [link?.videoPath]
    );

    const displayName = useMemo(
        () => link?.videoName || t("videoReview.gallery.defaultVideoName"),
        [link?.videoName, t]
    );

    if (status === "loading") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <BackHomeIcon to={PANEL_PATH} />
                <div style={{ textAlign: "center", padding: "3rem 0" }}>
                    <Spin size="large" />
                </div>
                <Footer />
            </>
        );
    }

    if (status === "invalid") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <BackHomeIcon to={PANEL_PATH} />
                <ErrorPanel message={t("videoReview.gallery.notFound")} />
                <Footer />
            </>
        );
    }

    if (status === "error") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <BackHomeIcon to={PANEL_PATH} />
                <ErrorPanel message={t("videoReview.gallery.loadFailed")} onRetry={loadLink} />
                <Footer />
            </>
        );
    }

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="video-review-page">
                <div className="video-review-page__title">
                    <div className="video-review-audit__toolbar">
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate("/v/manage")}
                        >
                            {t("videoReview.audit.backToManage")}
                        </Button>
                    </div>
                    <h2>{displayName}</h2>
                    <div className="video-review-audit__meta">
                        <Tag color="blue">{t("videoReview.audit.badge")}</Tag>
                        {link.expired ? (
                            <Tag>{t("videoReview.admin.expiredTag")}</Tag>
                        ) : (
                            <Tag color="green">{t("videoReview.admin.validTag")}</Tag>
                        )}
                        {link.commentVisibility === "individual" && (
                            <Tag>{t("videoReview.admin.visibilityIndividual")}</Tag>
                        )}
                        <Text type="secondary">
                            {t("videoReview.admin.commentCount", {
                                count: link.commentCount ?? 0,
                            })}
                        </Text>
                    </div>
                </div>

                <div className="video-review-layout">
                    <div className="video-review-layout__player">
                        {videoUrl ? (
                            <VideoReviewPlayer
                                ref={playerRef}
                                src={videoUrl}
                                videoName={displayName}
                                onTimeUpdate={setCurrentTimeMs}
                            />
                        ) : (
                            <ErrorPanel message={t("videoReview.gallery.videoMissing")} />
                        )}
                    </div>
                    <VideoReviewComments
                        linkId={link.id}
                        playerRef={playerRef}
                        currentTimeMs={currentTimeMs}
                        adminMode
                    />
                </div>
            </div>
            <Footer />
        </>
    );
}

export default VideoReviewAudit;
